import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Image, Input, Modal, Spin, Upload, message } from "antd";
import { LikeFilled, LikeOutlined, PlusOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { EasyAvatar } from "fin-web-components";
import { observer } from "mobx-react-lite";
import { uploadAppFileToOss } from "../../global/OssUploadApi";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { QaFeedApi } from "./QaFeedApi";
import type { QaFeedQuestion, QaFeedTab } from "./QaFeedType";
import "./QaFeedPage.css";

const PAGE_SIZE = 8;
const MAX_IMAGES = 3;

function displayTime(v: string): string {
  const d = dayjs(v);
  return d.isValid() ? d.format("M月D日 HH:mm") : v;
}

function QuestionPhotos({ urls }: { urls: string[] }) {
  const list = urls.slice(0, MAX_IMAGES);
  if (list.length === 0) return null;
  if (list.length === 1) {
    return (
      <Image.PreviewGroup>
        <div className="lf-qa-photos-single">
          <Image src={list[0]} alt="" />
        </div>
      </Image.PreviewGroup>
    );
  }
  return (
    <Image.PreviewGroup>
      <div className="lf-qa-photos-thumbs">
        {list.map((src, i) => (
          <div key={`${src}-${i}`} className="lf-qa-photos-thumb">
            <Image src={src} alt="" preview={{ mask: false }} />
          </div>
        ))}
      </div>
    </Image.PreviewGroup>
  );
}

function ReplyBlock({
  answerText,
  answeredAt,
  answererNickname,
  answererAvatar,
}: {
  answerText: string;
  answeredAt: string;
  answererNickname?: string;
  answererAvatar?: string;
}) {
  const name = answererNickname?.trim() || "老师";
  return (
    <div className="lf-qa-card__reply">
      <EasyAvatar
        shape="circle"
        size={26}
        url={answererAvatar?.trim() || undefined}
        word={avatarDisplayWord(name)}
        alt=""
        className="lf-qa-card__reply-avatar"
      />
      <div className="lf-qa-card__reply-main">
        <p className="lf-qa-card__reply-line">
          <span className="lf-qa-card__reply-author">{name}</span>
          <span>：</span>
          {answerText}
        </p>
        {answeredAt ? <div className="lf-qa-card__reply-meta">{displayTime(answeredAt)}</div> : null}
      </div>
    </div>
  );
}

export interface QaFeedPageProps {
  spaceId: string;
  spaceProductId: string;
  qaAllowPublic?: boolean;
}

const QaFeedPage: React.FC<QaFeedPageProps> = observer(
  ({ spaceId, spaceProductId, qaAllowPublic = false }) => {
    const showPublicTab = qaAllowPublic;
    const [tab, setTab] = useState<QaFeedTab>(showPublicTab ? "public" : "mine");
    const [items, setItems] = useState<QaFeedQuestion[]>([]);
    const [total, setTotal] = useState(0);
    const [pageNum, setPageNum] = useState(1);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const [askOpen, setAskOpen] = useState(false);
    const [askText, setAskText] = useState("");
    const [askImages, setAskImages] = useState<string[]>([]);
    const [uploadingImg, setUploadingImg] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const sentinelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (!showPublicTab && tab === "public") {
        setTab("mine");
      }
    }, [showPublicTab, tab]);

    const reloadFirstPage = useCallback(async () => {
      setLoading(true);
      try {
        const activeTab = showPublicTab ? tab : "mine";
        const res = await QaFeedApi.listFeed({
          tab: activeTab,
          pageNum: 1,
          pageSize: PAGE_SIZE,
          spaceId,
          spaceProductId,
        });
        if (typeof res === "string") {
          message.error(res);
          setItems([]);
          setTotal(0);
          setPageNum(1);
          return;
        }
        setItems(res.list ?? []);
        setTotal(res.total ?? 0);
        setPageNum(1);
      } finally {
        setLoading(false);
      }
    }, [tab, showPublicTab, spaceId, spaceProductId]);

    useEffect(() => {
      void reloadFirstPage();
    }, [reloadFirstPage]);

    const loadMore = useCallback(async () => {
      if (loading || loadingMore) return;
      if (total === 0 || items.length >= total) return;
      setLoadingMore(true);
      try {
        const activeTab = showPublicTab ? tab : "mine";
        const nextPage = pageNum + 1;
        const res = await QaFeedApi.listFeed({
          tab: activeTab,
          pageNum: nextPage,
          pageSize: PAGE_SIZE,
          spaceId,
          spaceProductId,
        });
        if (typeof res === "string") {
          message.error(res);
          return;
        }
        setItems((prev) => [...prev, ...(res.list ?? [])]);
        setPageNum(nextPage);
      } finally {
        setLoadingMore(false);
      }
    }, [items.length, loading, loadingMore, pageNum, tab, total, showPublicTab, spaceId, spaceProductId]);

    useEffect(() => {
      const el = sentinelRef.current;
      if (!el) return undefined;
      const ob = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) void loadMore();
        },
        { root: null, rootMargin: "100px", threshold: 0 }
      );
      ob.observe(el);
      return () => ob.disconnect();
    }, [loadMore]);

    const patchItem = useCallback((id: string, patch: Partial<QaFeedQuestion>) => {
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    }, []);

    const toggleLike = async (e: React.MouseEvent, row: QaFeedQuestion) => {
      e.stopPropagation();
      const res = await QaFeedApi.toggleLike({ spaceId, spaceProductId, questionId: row.id });
      if (typeof res === "string") {
        message.error(res);
        return;
      }
      patchItem(row.id, {
        likedByMe: res.liked,
        likeCount: res.likeCount,
        collectCount: res.collectCount,
        collectedByMe: res.collected,
      });
    };

    const toggleCollect = async (e: React.MouseEvent, row: QaFeedQuestion) => {
      e.stopPropagation();
      const res = await QaFeedApi.toggleCollect({ spaceId, spaceProductId, questionId: row.id });
      if (typeof res === "string") {
        message.error(res);
        return;
      }
      patchItem(row.id, {
        likedByMe: res.liked,
        likeCount: res.likeCount,
        collectCount: res.collectCount,
        collectedByMe: res.collected,
      });
    };

    const uploadImage = async (file: File) => {
      if (askImages.length >= MAX_IMAGES) {
        message.warning(`最多上传 ${MAX_IMAGES} 张图片`);
        return;
      }
      setUploadingImg(true);
      try {
        const url = await uploadAppFileToOss(file, "QA_QUESTION_IMAGE");
        if (url) setAskImages((prev) => [...prev, url].slice(0, MAX_IMAGES));
      } catch (err) {
        message.error(err instanceof Error ? err.message : "上传失败");
      } finally {
        setUploadingImg(false);
      }
    };

    const removeAskImage = (src: string) => {
      setAskImages((prev) => prev.filter((x) => x !== src));
    };

    const submitQuestion = async () => {
      const text = askText.trim();
      if (!text) {
        message.warning("请填写问题描述");
        return;
      }
      setSubmitting(true);
      try {
        const res = await QaFeedApi.submitQuestion({
          spaceId,
          spaceProductId,
          text,
          imageUrls: askImages,
        });
        if (typeof res === "string") {
          message.error(res);
          return;
        }
        message.success("提问已提交，请等待老师回复");
        setAskOpen(false);
        setAskText("");
        setAskImages([]);
        setTab("mine");
      } finally {
        setSubmitting(false);
      }
    };

    const activeTab = showPublicTab ? tab : "mine";
    const showEnd = !loading && total > 0 && items.length >= total;

    return (
      <div className="lf-qa-feed">
        <div className="lf-qa-feed__inner">
          <div className="lf-qa-feed__toolbar">
            <div className="lf-qa-feed__filters">
              {showPublicTab ? (
                <button
                  type="button"
                  className={`lf-qa-feed__filter${activeTab === "public" ? " lf-qa-feed__filter--on" : ""}`}
                  onClick={() => setTab("public")}
                >
                  会员可见
                </button>
              ) : null}
              <button
                type="button"
                className={`lf-qa-feed__filter${activeTab === "mine" ? " lf-qa-feed__filter--on" : ""}`}
                onClick={() => setTab("mine")}
              >
                我的提问
              </button>
            </div>
            <Button
              type="primary"
              className="lf-qa-feed__ask-btn"
              style={{ borderRadius: 5 }}
              onClick={() => setAskOpen(true)}
            >
              我要提问
            </Button>
          </div>

          <Spin spinning={loading}>
            {items.length === 0 && !loading ? (
              <div className="lf-qa-empty">
                {activeTab === "public"
                  ? "暂无公开问答，运营审核公开后将展示在此"
                  : "您还没有提问，点击右上角提交问题"}
              </div>
            ) : (
              <div className="lf-qa-feed__list">
                {items.map((row) => (
                  <article key={row.id} className="lf-qa-card">
                    <div className="lf-qa-card__head">
                      <EasyAvatar
                        shape="square"
                        squareRadius={4}
                        size={30}
                        style={{marginTop:3}}
                        url={row.askerAvatar?.trim() || undefined}
                        word={avatarDisplayWord(row.askerNickname, row.askerDisplayId)}
                        alt=""
                      />
                      <div className="lf-qa-card__main">
                        <div className="lf-qa-card__user-line">
                          <div className="lf-qa-card__user-info">
                            <span className="lf-qa-card__asker">
                              {row.askerNickname}
                              {row.isMine ? <span className="lf-qa-card__asker-me">（我）</span> : null}
                            </span>
                            <span className="lf-qa-card__time">{displayTime(row.askedAt)}</span>
                          </div>
                          {qaAllowPublic ? (
                            <div className="lf-qa-card__action-btns">
                              <button
                                type="button"
                                className="lf-qa-action-btn"
                                onClick={(e) => void toggleLike(e, row)}
                              >
                                {row.likedByMe ? (
                                  <LikeFilled className="lf-qa-action-ic lf-qa-action-ic--on" />
                                ) : (
                                  <LikeOutlined className="lf-qa-action-ic" />
                                )}
                                <span>{row.likeCount ?? 0}</span>
                              </button>
                              <button
                                type="button"
                                className="lf-qa-action-btn"
                                onClick={(e) => void toggleCollect(e, row)}
                              >
                                {row.collectedByMe ? (
                                  <StarFilled className="lf-qa-action-ic lf-qa-action-ic--collect-on" />
                                ) : (
                                  <StarOutlined className="lf-qa-action-ic" />
                                )}
                                <span>{row.collectCount ?? 0}</span>
                              </button>
                            </div>
                          ) : null}
                        </div>
                        <div className="lf-qa-card__text">{row.text}</div>
                        {row.images?.length ? (
                          <div className="lf-qa-card__photos">
                            <QuestionPhotos urls={row.images} />
                          </div>
                        ) : null}
                        {row.answers?.map((ans, idx) => (
                          <ReplyBlock
                            key={`${row.id}-ans-${idx}`}
                            answerText={ans.answerText}
                            answeredAt={ans.answeredAt}
                            answererNickname={ans.answererNickname}
                            answererAvatar={ans.answererAvatar}
                          />
                        ))}
                        {!row.answers?.length && row.isMine ? (
                          <div className="lf-qa-card__badge">等待老师回复</div>
                        ) : null}
                        {showPublicTab && row.isPublic ? (
                          <div className="lf-qa-card__meta">
                            <span className="lf-qa-card__badge">所有会员可见</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </article>
                ))}
                <div ref={sentinelRef} className="lf-qa-sentinel" />
                {loadingMore ? (
                  <div className="lf-qa-loading-more">
                    <Spin size="small" /> 加载中…
                  </div>
                ) : null}
                {showEnd ? <div className="lf-qa-end-hint">已经到底啦</div> : null}
              </div>
            )}
          </Spin>
        </div>

        <Modal
          className="lf-qa-modal"
          title="我要提问"
          open={askOpen}
          onCancel={() => {
            if (submitting) return;
            setAskOpen(false);
          }}
          destroyOnHidden
          footer={
            <div className="lf-qa-modal-footer">
              {qaAllowPublic ? (
                <span className="lf-qa-modal-hint">
                  您的提问将有概率被推荐到所有会员可见。
                </span>
              ) : (
                <span className="lf-qa-modal-hint lf-qa-modal-hint--empty" />
              )}
              <div className="lf-qa-modal-footer-btns">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => setAskOpen(false)}
                  style={{
                    width: 80,
                    height: 32,
                    margin: 0,
                    padding: 0,
                    boxSizing: "border-box",
                    borderRadius: 4,
                    border: "1px solid #d9d9d9",
                    background: "#fff",
                    color: "rgba(0, 0, 0, 0.88)",
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: "30px",
                    cursor: submitting ? "not-allowed" : "pointer",
                    flexShrink: 0,
                  }}
                >
                  取消
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => void submitQuestion()}
                  style={{
                    width: 80,
                    height: 32,
                    margin: 0,
                    padding: 0,
                    boxSizing: "border-box",
                    borderRadius: 4,
                    border: "1px solid #ec407a",
                    background: "#ec407a",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: "30px",
                    cursor: submitting ? "not-allowed" : "pointer",
                    opacity: submitting ? 0.65 : 1,
                    flexShrink: 0,
                  }}
                >
                  提交
                </button>
              </div>
            </div>
          }
        >
          <Input.TextArea
            className="lf-qa-modal-textarea"
            value={askText}
            onChange={(e) => setAskText(e.target.value)}
            placeholder="请描述您的问题，可补充背景与诉求"
            autoSize={{ minRows: 4, maxRows: 10 }}
            bordered={false}
          />
          <div className="lf-qa-modal-uploads">
            {askImages.map((src) => (
              <div key={src} className="lf-qa-modal-upload-item">
                <img src={src} alt="" className="lf-qa-modal-upload-thumb" />
                <button
                  type="button"
                  className="lf-qa-modal-upload-remove"
                  onClick={() => removeAskImage(src)}
                  aria-label="移除图片"
                >
                  ×
                </button>
              </div>
            ))}
            {askImages.length < MAX_IMAGES ? (
              <Upload
                showUploadList={false}
                accept="image/*"
                disabled={uploadingImg}
                beforeUpload={(file) => {
                  void uploadImage(file);
                  return false;
                }}
              >
                <button type="button" className="lf-qa-modal-upload-add" disabled={uploadingImg}>
                  {uploadingImg ? <Spin size="small" /> : <PlusOutlined />}
                </button>
              </Upload>
            ) : null}
          </div>
        </Modal>
      </div>
    );
  }
);

export default QaFeedPage;
